import { useContext, useState, useEffect } from "react";
import { AppContext } from "../App";
import { HiOutlineArrowNarrowRight, HiOutlineArrowLeft, HiOutlineArrowRight, HiOutlinePlay } from "react-icons/hi";

import banner1 from "../assets/banner1.jpg";
import banner2 from "../assets/banner2.jpg";
import banner3 from "../assets/banner3.jpg";
import banner4 from "../assets/banner4.jpg";
import banner5 from "../assets/banner5.jpg";

const sliderData = [
  { img: banner1, title: "Mirabel Suites", desc: "Privately owned hospitality and property management for short-term stays in Tanzania.", tag: "01" },
  { img: banner2, title: "Property Leasing", desc: "We lease, furnish, brand, market, and manage apartments, villas, houses, and serviced homes.", tag: "02" },
  { img: banner3, title: "Corporate Stays", desc: "Comfortable, secure, and fully managed accommodation for expatriates and business travellers.", tag: "03" },
  { img: banner4, title: "Guest Support", desc: "Reservation handling, concierge help, airport transfers, and travel coordination.", tag: "04" },
  { img: banner5, title: "Local Experiences", desc: "Curated tours, cultural experiences, and authentic Tanzanian hospitality.", tag: "05" }
];

const stats = [
  { num: "05", label: "Core Services" },
  { num: "24/7", label: "Guest Support" },
  { num: "TZ", label: "Tanzania Focus" }
];

export default function HomeBanner1() {
  const appContext = useContext(AppContext);
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);

  const handleNext = () => setActiveIdx((prev) => (prev + 1) % sliderData.length);
  const handlePrev = () => setActiveIdx((prev) => (prev - 1 + sliderData.length) % sliderData.length);

  useEffect(() => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          handleNext();
          return 0;
        }
        return p + 100 / 60;
      });
    }, 60);
    return () => clearInterval(interval);
  }, [activeIdx]);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@300;400;500;600;700;800;900&display=swap');
        
        :root {
          --sunflower-amber: #BC9132;
          --sunflower-light: #E8C547;
          --text-navy: #012243;
          --text-slate: #64748B;
          --border-subtle: rgba(255, 255, 255, 0.1);
          --font-corporate: 'Libre Franklin', sans-serif;
        }

        .hero-v2 {
          position: relative;
          min-height: 100vh;
          width: 100%;
          font-family: var(--font-corporate);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        /* --- BACKGROUND LAYERS --- */
        .hero-bg-image {
          position: absolute;
          inset: 0;
          background-image: url(${banner1});
          background-size: cover;
          background-position: center;
          z-index: 0;
          animation: slowZoom 20s ease-in-out infinite alternate;
        }

        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }

        /* Multi-layer overlay for depth and readability */
        .hero-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background: 
            /* Dark gradient from left to right for text readability */
            linear-gradient(105deg, 
              rgba(10, 15, 28, 0.95) 0%, 
              rgba(10, 15, 28, 0.85) 30%, 
              rgba(10, 15, 28, 0.6) 50%, 
              rgba(10, 15, 28, 0.4) 70%, 
              rgba(10, 15, 28, 0.7) 100%),
            /* Bottom fade for depth */
            linear-gradient(180deg, 
              transparent 0%, 
              rgba(10, 15, 28, 0.3) 100%);
        }

        /* Subtle noise texture for premium feel */
        .hero-noise {
          position: absolute;
          inset: 0;
          z-index: 2;
          opacity: 0.03;
          pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
        }

        /* Animated gradient orbs */
        .orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          opacity: 0.25;
          pointer-events: none;
          z-index: 3;
          animation: float 20s ease-in-out infinite;
        }
        .orb-1 { width: 600px; height: 600px; background: var(--sunflower-amber); top: -200px; right: -150px; }
        .orb-2 { width: 500px; height: 500px; background: #4A7C59; bottom: -150px; left: -150px; animation-delay: -7s; opacity: 0.15; }
        .orb-3 { width: 400px; height: 400px; background: var(--sunflower-light); top: 40%; left: 30%; animation-delay: -14s; opacity: 0.2; }

        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(50px, -30px) scale(1.1); }
          66% { transform: translate(-30px, 40px) scale(0.95); }
        }

        /* Top navigation bar */
        .hero-nav-v2 {
          position: relative;
          z-index: 10;
          padding: 28px 48px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .brand-mark {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .brand-logo {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: var(--sunflower-amber);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          font-size: 1.1rem;
          letter-spacing: -0.02em;
        }

        .brand-text {
          display: flex;
          flex-direction: column;
          line-height: 1.1;
        }
        .brand-text strong { font-size: 0.95rem; font-weight: 700; color: #fff; letter-spacing: -0.01em; }
        .brand-text span { font-size: 0.7rem; color: rgba(255,255,255,0.6); text-transform: uppercase; letter-spacing: 0.15em; font-weight: 500; }

        .nav-status {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 18px;
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 100px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
        .nav-status .pulse {
          width: 8px;
          height: 8px;
          background: #10B981;
          border-radius: 50%;
          position: relative;
        }
        .nav-status .pulse::after {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          background: #10B981;
          opacity: 0.4;
          animation: pulse 2s ease-out infinite;
        }
        @keyframes pulse {
          0% { transform: scale(0.8); opacity: 0.4; }
          100% { transform: scale(2); opacity: 0; }
        }

        /* Main content area */
        .hero-main-v2 {
          flex: 1;
          position: relative;
          z-index: 5;
          padding: 0 48px 48px;
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 60px;
          align-items: center;
        }

        .hero-left-v2 {
          max-width: 680px;
        }

        .hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          margin-bottom: 28px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: var(--sunflower-amber);
        }
        .hero-eyebrow::before {
          content: '';
          width: 40px;
          height: 1px;
          background: var(--sunflower-amber);
        }

        .hero-title-v2 {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 900;
          color: #fff;
          line-height: 0.95;
          letter-spacing: -0.04em;
          margin: 0 0 32px;
          text-shadow: 0 2px 40px rgba(0,0,0,0.3);
        }
        .hero-title-v2 .accent {
          font-style: italic;
          font-weight: 300;
          color: var(--sunflower-amber);
          display: block;
        }

        .hero-desc-v2 {
          font-size: 1.05rem;
          line-height: 1.7;
          color: rgba(255,255,255,0.75);
          max-width: 520px;
          margin-bottom: 48px;
        }

        .hero-cta-group {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-bottom: 64px;
        }

        .cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 14px;
          padding: 20px 32px;
          background: var(--sunflower-amber);
          color: var(--text-navy);
          border-radius: 100px;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          position: relative;
          overflow: hidden;
          border: 1px solid var(--sunflower-amber);
        }
        .cta-primary::before {
          content: '';
          position: absolute;
          inset: 0;
          background: #fff;
          transform: translateX(-100%);
          transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
          z-index: 0;
        }
        .cta-primary:hover::before { transform: translateX(0); }
        .cta-primary span, .cta-primary svg { position: relative; z-index: 1; }
        .cta-primary:hover { 
          transform: translateY(-2px); 
          box-shadow: 0 20px 40px -10px rgba(188, 145, 50, 0.6);
          border-color: #fff;
        }

        .cta-secondary {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 14px 0;
          color: #fff;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          border-bottom: 1px solid rgba(255,255,255,0.3);
          transition: all 0.3s ease;
        }
        .cta-secondary:hover { color: var(--sunflower-amber); border-color: var(--sunflower-amber); gap: 18px; }

        /* Stats row */
        .stats-row {
          display: flex;
          gap: 48px;
          padding-top: 32px;
          border-top: 1px solid rgba(255,255,255,0.15);
        }
        .stat-item { display: flex; flex-direction: column; }
        .stat-num {
          font-size: 2.2rem;
          font-weight: 800;
          color: #fff;
          letter-spacing: -0.03em;
          line-height: 1;
          margin-bottom: 6px;
        }
        .stat-label {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.6);
          text-transform: uppercase;
          letter-spacing: 0.15em;
          font-weight: 600;
        }

        /* Right side - Reel carousel */
        .hero-right-v2 {
          position: relative;
          height: 600px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .reel-stage {
          position: relative;
          width: 100%;
          height: 100%;
          perspective: 1500px;
        }

        .reel-card {
          position: absolute;
          inset: 0;
          border-radius: 24px;
          overflow: hidden;
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.6);
          border: 1px solid rgba(255,255,255,0.1);
        }

        .reel-card img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 1.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reel-card.active img { transform: scale(1.05); }

        .reel-card::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 40%, rgba(10, 15, 28, 0.9) 100%);
        }

        .reel-card.active {
          transform: translateX(0) rotateY(0) scale(1);
          opacity: 1;
          z-index: 5;
        }
        .reel-card.next {
          transform: translateX(40px) rotateY(-8deg) scale(0.92);
          opacity: 0.6;
          z-index: 4;
        }
        .reel-card.prev {
          transform: translateX(-40px) rotateY(8deg) scale(0.92);
          opacity: 0.6;
          z-index: 4;
        }
        .reel-card.hidden {
          transform: translateX(0) scale(0.85);
          opacity: 0;
          z-index: 1;
          pointer-events: none;
        }

        .reel-meta {
          position: absolute;
          bottom: 32px;
          left: 32px;
          right: 32px;
          z-index: 10;
          color: #fff;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.6s ease 0.3s;
        }
        .reel-card.active .reel-meta { opacity: 1; transform: translateY(0); }

        .reel-tag {
          display: inline-block;
          padding: 6px 12px;
          background: rgba(188, 145, 50, 0.2);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(188, 145, 50, 0.4);
          border-radius: 100px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          color: var(--sunflower-light);
          margin-bottom: 16px;
        }
        .reel-title {
          font-size: 1.8rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          margin: 0 0 10px;
          line-height: 1.1;
        }
        .reel-desc {
          font-size: 0.9rem;
          line-height: 1.6;
          color: rgba(255,255,255,0.8);
          margin: 0;
          max-width: 90%;
        }

        /* Progress bar */
        .progress-track {
          position: absolute;
          top: 24px;
          left: 32px;
          right: 32px;
          height: 2px;
          background: rgba(255,255,255,0.2);
          border-radius: 2px;
          overflow: hidden;
          z-index: 10;
        }
        .progress-fill {
          height: 100%;
          background: var(--sunflower-amber);
          width: 0%;
          transition: width 0.06s linear;
        }

        /* Counter badge */
        .counter-badge {
          position: absolute;
          top: 24px;
          right: 32px;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 100px;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.1em;
        }
        .counter-badge strong { font-size: 1rem; font-weight: 800; }

        /* Navigation arrows */
        .reel-nav {
          position: absolute;
          bottom: -20px;
          right: 0;
          display: flex;
          gap: 12px;
          z-index: 20;
        }
        .reel-btn {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.3);
        }
        .reel-btn:hover {
          background: var(--sunflower-amber);
          border-color: var(--sunflower-amber);
          color: var(--text-navy);
          transform: translateY(-3px);
          box-shadow: 0 15px 40px -10px rgba(188, 145, 50, 0.5);
        }

        /* Floating play button */
        .play-fab {
          position: absolute;
          top: 40px;
          left: -30px;
          z-index: 15;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: var(--sunflower-amber);
          color: var(--text-navy);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border: none;
          transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          box-shadow: 0 20px 40px -10px rgba(188, 145, 50, 0.6);
        }
        .play-fab::before {
          content: '';
          position: absolute;
          inset: -8px;
          border-radius: 50%;
          border: 1px solid var(--sunflower-amber);
          opacity: 0.3;
          animation: ring 2s ease-out infinite;
        }
        @keyframes ring {
          0% { transform: scale(1); opacity: 0.3; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .play-fab:hover { transform: scale(1.1) rotate(10deg); }

        /* Scroll indicator */
        .scroll-indicator {
          position: absolute;
          bottom: 40px;
          left: 48px;
          z-index: 10;
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: rgba(255,255,255,0.6);
        }
        .scroll-line {
          width: 40px;
          height: 1px;
          background: rgba(255,255,255,0.3);
          position: relative;
          overflow: hidden;
        }
        .scroll-line::after {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--sunflower-amber);
          animation: scrollAnim 2s ease-in-out infinite;
        }
        @keyframes scrollAnim {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0); }
          100% { transform: translateX(100%); }
        }

        /* Responsive */
        @media (max-width: 1100px) {
          .hero-main-v2 { grid-template-columns: 1fr; gap: 40px; padding: 0 32px 32px; }
          .hero-right-v2 { height: 500px; }
          .hero-nav-v2 { padding: 24px 32px; }
          .scroll-indicator { left: 32px; }
          .play-fab { left: -10px; width: 64px; height: 64px; }
        }

        @media (max-width: 768px) {
          .hero-nav-v2 { padding: 20px 20px; }
          .brand-text { display: none; }
          .hero-main-v2 { padding: 0 20px 20px; }
          .hero-title-v2 { font-size: 3rem; }
          .hero-cta-group { flex-direction: column; align-items: flex-start; margin-bottom: 40px; }
          .cta-primary, .cta-secondary { width: 100%; justify-content: center; }
          .cta-secondary { width: auto; }
          .stats-row { gap: 24px; flex-wrap: wrap; }
          .stat-num { font-size: 1.6rem; }
          .hero-right-v2 { height: 450px; }
          .reel-title { font-size: 1.3rem; }
          .reel-desc { font-size: 0.8rem; }
          .reel-meta { bottom: 24px; left: 24px; right: 24px; }
          .reel-nav { bottom: -10px; }
          .reel-btn { width: 48px; height: 48px; }
          .play-fab { display: none; }
          .scroll-indicator { display: none; }
        }

        @media (max-width: 480px) {
          .hero-title-v2 { font-size: 2.4rem; }
          .hero-right-v2 { height: 400px; }
          .counter-badge { padding: 6px 10px; font-size: 0.65rem; }
          .counter-badge strong { font-size: 0.85rem; }
        }
      `}</style>

      <section className="hero-v2">
        {/* Background layers */}
        <div className="hero-bg-image" />
        <div className="hero-overlay" />
        <div className="hero-noise" />
        
        {/* Animated gradient orbs */}
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />

        {/* Top navigation */}
        <div className="hero-nav-v2 d-none">
          <div className="brand-mark">
            <div className="brand-logo">DL</div>
            <div className="brand-text">
              <strong>Mirabel Suites</strong>
              <span>Hospitality &amp; Property Management</span>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="hero-main-v2 pt-5">
          <div className="hero-left-v2">
            <div className="hero-eyebrow">Mirabel Suites</div>
            <h1 className="hero-title-v2">
              Hospitality
              <span className="accent">with care</span>
              in Tanzania.
            </h1>
            <p className="hero-desc-v2">
              A privately owned hospitality company focused on short-term accommodation, property management, travel support, and authentic local experiences.
            </p>

            <div className="hero-cta-group">
              <a 
                href="#services" 
                className="cta-primary"
                onClick={(e) => { e.preventDefault(); appContext?.navTo?.({ item: "services" }); }}
              >
                <span>Explore Services</span>
                <HiOutlineArrowNarrowRight size={18} />
              </a>
            </div>

            <div className="stats-row">
              {stats.map((s, i) => (
                <div key={i} className="stat-item">
                  <div className="stat-num">{s.num}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-right-v2">
            <div className="reel-stage">
              {sliderData.map((item, index) => {
                let state = "hidden";
                const diff = (index - activeIdx + sliderData.length) % sliderData.length;
                if (diff === 0) state = "active";
                else if (diff === 1) state = "next";
                else if (diff === sliderData.length - 1) state = "prev";

                return (
                  <div 
                    key={index} 
                    className={`reel-card ${state}`}
                    onClick={() => setActiveIdx(index)}
                  >
                    <img src={item.img} alt={item.title} />
                    <div className="progress-track">
                      <div className="progress-fill" style={{ width: index === activeIdx ? `${progress}%` : '0%' }} />
                    </div>
                    <div className="counter-badge">
                      <strong>{String(index + 1).padStart(2, '0')}</strong> / {String(sliderData.length).padStart(2, '0')}
                    </div>
                    <div className="reel-meta">
                      <span className="reel-tag">{item.tag} · Feature</span>
                      <h3 className="reel-title">{item.title}</h3>
                      <p className="reel-desc">{item.desc}</p>
                    </div>
                  </div>
                );
              })}

              <button className="play-fab" aria-label="Watch overview">
                <HiOutlinePlay size={28} />
              </button>
            </div>

            <div className="reel-nav">
              <button className="reel-btn" onClick={handlePrev} aria-label="Previous">
                <HiOutlineArrowLeft size={20} />
              </button>
              <button className="reel-btn" onClick={handleNext} aria-label="Next">
                <HiOutlineArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <span className="scroll-line"></span>
          Scroll to explore
        </div>
      </section>
    </>
  );
}
